import { ClipboardButton, Skeleton, Typography } from '@neo4j-ndl/react';
import { ChevronRightIconOutline, DbmsIcon, FolderIconOutline, UserGroupIconOutline } from '@neo4j-ndl/react/icons';
import { ACTION, type ProjectSummary, consoleApi, usePermissions, useUnsafeAppContext } from '@nx/state';
import { Grid } from '@nx/ui';
import cx from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router';

export const NewCountIndicator = ({ icon, count, label }: { icon: React.ReactNode; count: number; label: string }) => {
  return (
    <div className="flex flex-row items-center gap-2 overflow-hidden">
      {icon}
      <Typography variant="body-medium" className="truncate">
        <span className="font-bold">{count}</span> <span className="text-neutral-text-weaker capitalize">{label}</span>
      </Typography>
    </div>
  );
};

export const newProjectCardCols = { min: 8, sm: 4, md: 6 };

export const NewProjectCard = React.forwardRef<HTMLDivElement, { projectSummary: ProjectSummary }>(
  ({ projectSummary }, ref) => {
    const { isAllowed } = usePermissions();
    const { updateActiveProject } = useUnsafeAppContext();
    const { data: project, isLoading, isSuccess } = consoleApi.useGetProjectQuery(projectSummary.id);
    const canReadProjectInstances = isAllowed(ACTION.READ, `namespaces/${projectSummary.id}/databases`);
    const canReadProjectMembers =
      isAllowed(ACTION.READ, `namespaces/${projectSummary.id}/users`) &&
      isAllowed(ACTION.READ, `namespaces/${projectSummary.id}/roles`);
    const navigate = useNavigate();
    const { data: user } = consoleApi.useGetUserDetailsQuery();

    // TODO fetch in parent, before rendering this card
    const { data: instances, isLoading: isLoadingInstances } = consoleApi.useListInstancesQuery(projectSummary.id, {
      skip: !canReadProjectInstances,
    });
    const { data: members, isLoading: isLoadingMembers } = consoleApi.useListProjectMembersQuery(projectSummary.id, {
      skip: !canReadProjectMembers,
    });

    const instancesCount = instances?.length ?? 0;
    const membersCount = members?.length ?? 0;

    if (isLoading || isLoadingInstances || isLoadingMembers) {
      return (
        <Skeleton as={Grid.Item} className="rounded-1xl h-36 w-full" htmlAttributes={{ cols: newProjectCardCols }} />
      );
    }

    if (!isSuccess) {
      return null;
    }

    const onClick = () => {
      updateActiveProject({
        orgId: project.organizationId,
        projectId: project.id,
      });
      void navigate(`/projects/${project.id}/instances`);
      localStorage.setItem(`default-project-${user?.id}`, project.id);
    };

    return (
      <Grid.Item key={project.id} cols={newProjectCardCols}>
        <div
          ref={ref}
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onClick();
            }
          }}
          className={cx(
            'focus-visible:outline-palette-primary-focus border-neutral-border-weak rounded-1xl bg-neutral-bg-weak hover:border-neutral-border-strong flex h-36 flex-row items-start gap-6 border p-6 outline-none hover:cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2',
          )}
        >
          <div className="bg-palette-neutral-bg-strong flex flex-row items-center rounded-md p-2">
            <FolderIconOutline className="text-palette-neutral-icon size-5" />
          </div>
          <div className="flex grow flex-col gap-4 overflow-hidden">
            <div className="flex w-full flex-col items-start gap-1">
              <Typography variant="h5" className="w-full truncate">
                {projectSummary.name}
              </Typography>
              <div className="flex w-full flex-row items-center gap-1 pr-1">
                <Typography variant="body-medium" className="text-neutral-text-weaker truncate italic">
                  {projectSummary.id}
                </Typography>
                <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                  <ClipboardButton
                    size="small"
                    textToCopy={projectSummary.id}
                    htmlAttributes={{ 'aria-label': 'Copy ID to Clipboard' }}
                  />
                </span>
              </div>
            </div>
            <div className="flex w-full flex-row items-center gap-3">
              <NewCountIndicator
                icon={<DbmsIcon className="text-palette-neutral-icon size-6 shrink-0" />}
                count={instancesCount}
                label={instancesCount === 1 ? 'Instance' : 'Instances'}
              />
              <NewCountIndicator
                icon={<UserGroupIconOutline className="text-palette-neutral-icon size-6 shrink-0" />}
                count={membersCount}
                label={membersCount === 1 ? 'Member' : 'Members'}
              />
              <ChevronRightIconOutline className="text-palette-neutral-icon ml-auto size-5 shrink-0" />
            </div>
          </div>
        </div>
      </Grid.Item>
    );
  },
);
NewProjectCard.displayName = 'NewProjectCard';
